var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useRef, useEffect } from "react";
import { Modal, ModalFooter, Button, Icon, Loading, MessageBanner, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { getProductNumberAssignmentByProductId, previewProductNumberAssignment, assignProductNumber, deselectProductNumber, } from "../../api/productNumberService";
import { assignQueryRetry, getAssignNoticeMessageId, } from "../../utils/productNumberAssignErrors";
import css from "./EntityRow.module.css";
import { getErrorCode } from "../../utils/getErrorCode";
import { resolveAssignErrorMessageId } from "../../utils/newPrefixMagnitude";
import { useNewPrefixMagnitude } from "../../hooks/useNewPrefixMagnitude";
import { NewPrefixMagnitudeChoice } from "../productNumber/NewPrefixMagnitudeChoice";
/**
 * Simple modal that assigns an ISBN/ISMN to a product in one step. When the
 * publisher already has a prefix, it shows the number that will be assigned;
 * when a new publisher prefix is required, it expands a block-size choice so
 * the whole thing is done here — no redirect to the standalone assign page.
 */
export var ProductNumberAssignModal = function (_a) {
    var productId = _a.productId, onClose = _a.onClose, onAssigned = _a.onAssigned, onError = _a.onError;
    var formatMessage = useIntl().formatMessage;
    var ky = useOkapiKy();
    // Opening the modal previews the assignment, which reserves the number
    // (selected = true) server-side. Track that reserved id so we can release it
    // if the user closes the modal without assigning — otherwise it stays selected
    // and the "next available number" lookup skips it, leaving a gap.
    var reservedProductNumberIdRef = useRef(null);
    var assignedRef = useRef(false);
    var _b = useQuery(["assignPreview", productId], function () { return getProductNumberAssignmentByProductId(ky, productId); }, {
        enabled: !!productId,
        cacheTime: 0,
        // Fail fast on a 4xx so the modal shows an error instead of a stuck spinner.
        retry: assignQueryRetry,
        onSuccess: function (data) {
            var _a, _b;
            reservedProductNumberIdRef.current = (_b = (_a = data === null || data === void 0 ? void 0 : data.productNumber) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null;
        },
    }), assignment = _b.data, isLoading = _b.isLoading, isError = _b.isError;
    // The backend read preview returns 200 with a reason when a precondition forbids
    // assignment; explain it and block the action rather than getting stuck.
    var blockedMessageId = getAssignNoticeMessageId(assignment);
    var releaseReservation = function () {
        var reservedId = reservedProductNumberIdRef.current;
        if (!reservedId || assignedRef.current) {
            return;
        }
        reservedProductNumberIdRef.current = null;
        // Best-effort cleanup: a failure here is covered by the server-side
        // scheduled safety-net, so we log rather than surface it to the user.
        deselectProductNumber(ky, reservedId).catch(function (error) {
            return console.error("Failed to release reserved product number:", error);
        });
    };
    var handleClose = function () {
        releaseReservation();
        onClose();
    };
    useEffect(function () {
        return function () {
            releaseReservation();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var needsPrefix = !!(assignment === null || assignment === void 0 ? void 0 : assignment.createNewPublisherPrefix);
    var automaticPrefixSize = !!(assignment === null || assignment === void 0 ? void 0 : assignment.newPrefixSizeAutomatic);
    // Block size of a new prefix, capped by the publisher's production (FSD PP20) — smallest allowed preselected.
    var _c = useNewPrefixMagnitude(assignment === null || assignment === void 0 ? void 0 : assignment.maxMagnitude, assignment === null || assignment === void 0 ? void 0 : assignment.allowedMagnitudes), magnitude = _c.magnitude, setMagnitude = _c.setMagnitude;
    var noNumbersAvailable = !!assignment && !assignment.ready && !needsPrefix;
    var assignMutation = useMutation(function () { return __awaiter(void 0, void 0, void 0, function () {
        var publisherId, previewPayload, preview, assignPayload;
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        return __generator(this, function (_k) {
            switch (_k.label) {
                case 0:
                    publisherId = assignment.publisherId;
                    if (needsPrefix) {
                        return [2 /*return*/, assignProductNumber(ky, {
                                productId: productId,
                                publisherId: publisherId,
                                magnitude: automaticPrefixSize ? 0 : magnitude,
                                agencyPrefixId: (_a = assignment === null || assignment === void 0 ? void 0 : assignment.agencyPrefix) === null || _a === void 0 ? void 0 : _a.id,
                            })];
                    }
                    previewPayload = {
                        productId: productId,
                        publisherId: publisherId,
                        magnitude: 0,
                    };
                    if ((_b = assignment === null || assignment === void 0 ? void 0 : assignment.agencyPrefix) === null || _b === void 0 ? void 0 : _b.id)
                        previewPayload.agencyPrefixId = assignment.agencyPrefix.id;
                    if ((_c = assignment === null || assignment === void 0 ? void 0 : assignment.publisherPrefix) === null || _c === void 0 ? void 0 : _c.id)
                        previewPayload.publisherPrefixId = assignment.publisherPrefix.id;
                    // Reuse the number already reserved when the modal opened. Passing it makes
                    // the server deselect and re-select the same number instead of skipping it
                    // and reserving the next one — which would assign a different number than
                    // the one shown and leave the reserved one orphaned.
                    if ((_d = assignment === null || assignment === void 0 ? void 0 : assignment.productNumber) === null || _d === void 0 ? void 0 : _d.id)
                        previewPayload.productNumberId = assignment.productNumber.id;
                    return [4 /*yield*/, previewProductNumberAssignment(ky, previewPayload)];
                case 1:
                    preview = _k.sent();
                    reservedProductNumberIdRef.current =
                        (_f = (_e = preview.productNumber) === null || _e === void 0 ? void 0 : _e.id) !== null && _f !== void 0 ? _f : reservedProductNumberIdRef.current;
                    assignPayload = {
                        productId: productId,
                        publisherId: publisherId,
                        magnitude: 0,
                    };
                    if ((_g = preview.productNumber) === null || _g === void 0 ? void 0 : _g.id)
                        assignPayload.productNumberId = preview.productNumber.id;
                    if ((_h = preview.agencyPrefix) === null || _h === void 0 ? void 0 : _h.id)
                        assignPayload.agencyPrefixId = preview.agencyPrefix.id;
                    if ((_j = preview.publisherPrefix) === null || _j === void 0 ? void 0 : _j.id)
                        assignPayload.publisherPrefixId = preview.publisherPrefix.id;
                    return [2 /*return*/, assignProductNumber(ky, assignPayload)];
            }
        });
    }); }, {
        onSuccess: function (result) {
            assignedRef.current = true;
            reservedProductNumberIdRef.current = null;
            onAssigned(result.fullFormattedValue ||
                result.formattedValue ||
                result.value ||
                (assignment === null || assignment === void 0 ? void 0 : assignment.assignedValue) ||
                "");
        },
        onError: function (error) { return __awaiter(void 0, void 0, void 0, function () {
            var code;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, getErrorCode(error)];
                    case 1:
                        code = _a.sent();
                        onError(formatMessage({
                            id: resolveAssignErrorMessageId(code, "ui-br-agency.product-number.assign.error.assignFailed", automaticPrefixSize),
                        }));
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var canAssign = !!(assignment === null || assignment === void 0 ? void 0 : assignment.publisherId) &&
        !assignMutation.isLoading &&
        !noNumbersAvailable &&
        !blockedMessageId &&
        !isError;
    var footer = (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", disabled: !canAssign, onClick: function () { return assignMutation.mutate(); } }, assignMutation.isLoading ? (React.createElement(React.Fragment, null,
            React.createElement(Icon, { icon: "spinner-ellipsis", size: "small" }),
            React.createElement(FormattedMessage, { id: "ui-br-agency.button.saving" }))) : (React.createElement(FormattedMessage, { id: "ui-br-agency.title.products.panel.assignNumber" }))),
        React.createElement(Button, { onClick: handleClose, disabled: assignMutation.isLoading },
            React.createElement(FormattedMessage, { id: "ui-br-agency.button.cancel" }))));
    return (React.createElement(Modal, { open: true, dismissible: true, onClose: handleClose, size: "small", label: formatMessage({
            id: "ui-br-agency.title.products.panel.assignNumber",
        }), footer: footer }, isLoading ? (React.createElement(Loading, null)) : isError || !assignment ? (React.createElement(MessageBanner, { type: "error" },
        React.createElement(FormattedMessage, { id: "ui-br-agency.product-number.assign.error.loadFailed" }))) : blockedMessageId ? (React.createElement("div", { className: css.assignBody },
        React.createElement(MessageBanner, { type: "warning" }, formatMessage({ id: blockedMessageId })),
        assignment.assignedValue && (React.createElement("div", { className: css.assignLine },
            React.createElement("span", { className: css.fieldLabel },
                React.createElement(FormattedMessage, { id: "ui-br-agency.product-number.assign.field.label.value" })),
            React.createElement("span", { className: css.assignValue }, assignment.assignedValue))))) : (React.createElement("div", { className: css.assignBody },
        assignment.publisherName && (React.createElement("div", { className: css.assignLine },
            React.createElement("span", { className: css.fieldLabel },
                React.createElement(FormattedMessage, { id: "ui-br-agency.product-number.assign.field.label.publisher" })),
            React.createElement("span", null, assignment.publisherName))),
        needsPrefix && !automaticPrefixSize && (React.createElement(NewPrefixMagnitudeChoice, { maxMagnitude: assignment.maxMagnitude, allowedMagnitudes: assignment.allowedMagnitudes, replacesUsedUpPrefix: assignment.replacesUsedUpPrefix, magnitude: magnitude, onChange: setMagnitude, name: "magnitude", fieldsetClassName: css.magnitudeFieldset, hintClassName: css.assignHint })),
        noNumbersAvailable && (React.createElement(MessageBanner, { type: "warning" },
            React.createElement(FormattedMessage, { id: "ui-br-agency.title.products.panel.assign.noNumbers" }))),
        !needsPrefix && !noNumbersAvailable && (React.createElement("div", { className: css.assignLine },
            React.createElement("span", { className: css.fieldLabel },
                React.createElement(FormattedMessage, { id: "ui-br-agency.product-number.assign.field.label.value" })),
            React.createElement("span", { className: css.assignValue }, assignment.assignedValue || "-")))))));
};
